import { isDropZonePinComponent, PinComponent, toJSONContent } from '@lessonup/pins-shared';
import { color, cx, styled } from '@lessonup/ui-components';
import React, { useMemo } from 'react';
import { TiptapViewer } from '../../components/Tiptap/TipTapViewer';
import { usePinContext } from '../../contexts/pinContext';
import { useEffectivePinComponentSettings } from '../../utils/pinTheme/PinThemeContext';
import type { DropZonePinComponentProps } from './DropZonePinComponent.types';
import { computeTextPinComponentStyles } from './DropZonePinComponent.utils';
import { useDropZoneHandlers } from './hooks/useDropZoneHandlers';

export const DropZonePinComponent: React.FC<DropZonePinComponentProps> = ({
  pinId,
  componentId,
  settings,
  editable,
  active,
  isLinked,
  Renderer,
  isTextEditMode,
  setModeTextEdit,
}: DropZonePinComponentProps) => {
  const dropZoneSettings = useEffectivePinComponentSettings('DROP_ZONE', {
    settings,
  });
  const pin = usePinContext();

  const content = typeof settings.textFill === 'string' ? toJSONContent(settings.textFill) : settings.textFill;

  const {
    registerEditor,
    dropZonePinComponentRef,
    handleMouseDown,
    handleTouchStart,
    handleMouseUpTouchEnd,
    handleMouseMoveTouchMove,
    handleOnContextMenu,
  } = useDropZoneHandlers({
    active,
    isTextEditMode,
    setModeTextEdit,
    componentId,
  });

  const styles = computeTextPinComponentStyles(dropZoneSettings);

  const backgroundStyle = dropZoneSettings.imageFill
    ? {
        backgroundImage: `url(${dropZoneSettings.imageFill.url})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
      }
    : {};

  const tag = useMemo(() => {
    const dropzoneIndex = pin.pinComponents
      .filter((c: PinComponent) => isDropZonePinComponent(c))
      .findIndex((d: PinComponent) => d.id === componentId);

    if (dropzoneIndex === -1) {
      return 'A';
    }

    // 0, 1, 2 -> A, B, C
    return String.fromCharCode(65 + dropzoneIndex);
  }, [pin.pinComponents, componentId]);

  return (
    <StyledDropZoneComponent
      className={cx(
        active ? 'active' : undefined,
        isLinked ? 'linked' : undefined,
        isTextEditMode ? 'edit' : undefined
      )}
      {...(editable && { 'data-tag': tag })}
      style={{
        ...backgroundStyle,
        border:
          dropZoneSettings.borderSize && dropZoneSettings.borderSize > 0
            ? `${dropZoneSettings.borderSize}px solid ${dropZoneSettings.borderColor ?? '#ff0000'}`
            : 'none',
        borderRadius: dropZoneSettings.borderRadius,
        backgroundColor: dropZoneSettings.backgroundColor,
        ...styles,
      }}
      onTouchStart={handleTouchStart}
      ref={dropZonePinComponentRef}
      onMouseDown={editable ? handleMouseDown : undefined}
      onMouseMove={editable ? handleMouseMoveTouchMove : undefined}
      onTouchMove={editable ? handleMouseMoveTouchMove : undefined}
      onMouseUp={editable ? handleMouseUpTouchEnd : undefined}
      onTouchEnd={editable ? handleMouseUpTouchEnd : undefined}
      onContextMenu={handleOnContextMenu}
    >
      <InnerBorder
        style={{
          border:
            dropZoneSettings.borderSize && dropZoneSettings.borderSize > 0
              ? `${dropZoneSettings.borderSize}px dashed ${dropZoneSettings.borderColor ?? '#ff0000'}`
              : 'none',
          borderRadius: dropZoneSettings.borderRadius ? dropZoneSettings.borderRadius - 4 : 0,
        }}
      >
        {Renderer ? (
          <Renderer
            pinId={pinId}
            pinComponentId={componentId}
            content={content}
            registerEditor={registerEditor}
            active={active}
            isTextEditMode={isTextEditMode}
            editable={editable}
            fragmentKey="textFill"
          />
        ) : (
          <TiptapViewer content={content} />
        )}
      </InnerBorder>
    </StyledDropZoneComponent>
  );
};

const StyledDropZoneComponent = styled.div`
  text-align: var(--component-horizontal-align);
  align-items: var(--component-vertical-align);
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  user-select: none;
  font-family: var(--component-fontfamily);
  font-size: var(--component-fontsize);
  color: var(--component-fontcolor);

  /* Only show ::before when data-tag attribute exists and has a value */
  &[data-tag]::before {
    content: attr(data-tag);
    position: absolute;
    top: -25px;
    left: 50%;
    transform: translateX(-50%);
    background: ${color.utils.focus.stroke};
    color: ${color.utils.luminocityLight.on};
    padding: 4px 10px;
    border-radius: 6px 6px 0 0;
    font-size: 12px;
    font-weight: 500;
    z-index: -1;
  }

  &.edit {
    *:focus .selected-text {
      background-color: initial;
    }
    .selected-text {
      background-color: ${color.additional.disabled.background};
    }
  }

  &.active {
    a {
      pointer-events: none;
    }
  }

  &:not(.edit) {
    cursor: default;
    caret-color: transparent;
    span::selection {
      background-color: transparent;
      color: inherit;
    }
  }

  .ProseMirror:focus {
    outline: none;
  }

  .ProseMirror p.is-editor-empty:first-of-type::before {
    display: inline-block;
    position: absolute;
    color: #292e3d;
    pointer-events: none;
    content: var(--component-placeholder-text);
    font-weight: var(--component-fontweight);
    pointer-events: none;
    left: var(--component-placeholder-left);
    right: var(--component-placeholder-right);
    transform: var(--component-placeholder-transform);
  }

  .collaboration-cursor__caret {
    border-left: 0.5px solid #0d0d0d;
    border-right: 0.5px solid #0d0d0d;
    margin-left: -0.5px;
    margin-right: -0.5px;
    pointer-events: none;
    position: relative;
    word-break: normal;
  }

  /* Render the username above the caret */
  .collaboration-cursor__label {
    border-radius: 1.5px 1.5px 1.5px 0;
    color: #ffffff;
    font-size: 6px;
    font-style: normal;
    font-weight: 600;
    left: -0.5px;
    line-height: normal;
    padding: 0.1rem 0.3rem;
    position: absolute;
    top: -0.7em;
    user-select: none;
    white-space: nowrap;
  }

  /* Image content styling */
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const InnerBorder = styled.div`
  width: calc(100% - 10px);
  height: calc(100% - 10px);
  padding: 0 1rem;
`;
